import { z } from 'zod'

export const USERNAME_MIN_LENGTH = 3
export const USERNAME_MAX_LENGTH = 20

/**
 * Normalizes email addresses by removing + aliases to prevent duplicate accounts
 * Examples:
 * - user+signup@gmail.com -> user@gmail.com
 * - user+test@resend.dev -> user@resend.dev
 * - user@domain.com -> user@domain.com (no change)
 */
export function normalizeEmail(email: string): string {
	const [localPart, domain] = email.toLowerCase().split('@')

	// Remove everything after + in the local part
	const normalizedLocal = localPart?.split('+')[0] ?? localPart

	return `${normalizedLocal}@${domain}`
}

export const UsernameSchema = z
	.string({
		error: (issue) =>
			issue.input === undefined
				? 'Username is required'
				: 'Username must be text',
	})
	.min(1, { error: 'Username is required' })
	.min(USERNAME_MIN_LENGTH, { error: 'Username is too short' })
	.max(USERNAME_MAX_LENGTH, { error: 'Username is too long' })
	.regex(/^[a-zA-Z0-9_]+$/, {
		error: 'Username can only include letters, numbers, and underscores',
	})
	// users can type the username in any case, but we store it in lowercase
	.transform((value) => value.toLowerCase())

export const PasswordSchema = z
	.string({
		error: (issue) =>
			issue.input === undefined
				? 'Password is required'
				: 'Password must be text',
	})
	.min(1, { error: 'Password is required' })
	.min(6, { error: 'Password is too short' })
	// NOTE: bcrypt has a limit of 72 bytes (which should be plenty long)
	// https://github.com/epicweb-dev/epic-stack/issues/918
	.refine((val) => new TextEncoder().encode(val).length <= 72, {
		error: 'Password is too long',
	})

export const NameSchema = z
	.string({
		error: (issue) =>
			issue.input === undefined ? 'Name is required' : 'Name must be text',
	})
	.min(1, { error: 'Name is required' })
	.min(3, { error: 'Name is too short' })
	.max(40, { error: 'Name is too long' })

export const EmailSchema = z
	.string({
		error: (issue) =>
			issue.input === undefined ? 'Email is required' : 'Email must be text',
	})
	.min(1, { error: 'Email is required' })
	.email({ error: 'Email is invalid' })
	.min(3, { error: 'Email is too short' })
	.max(100, { error: 'Email is too long' })
	// users can type the email in any case, but we store it in lowercase
	.transform((value) => value.toLowerCase())

export const PasswordAndConfirmPasswordSchema = z
	.object({ password: PasswordSchema, confirmPassword: PasswordSchema })
	.superRefine(({ confirmPassword, password }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				path: ['confirmPassword'],
				code: 'custom',
				message: 'The passwords must match',
			})
		}
	})
